import { useCallback, useSyncExternalStore } from 'react';
/**
 * Web defaults to Portuguese. Android installs its resource catalogue and effective language
 * before rendering; an existing explicit choice is restored by the client.
 */
let locale = 'pt-BR';
let translator;
let snapshot = { locale, translator };
const listeners = new Set();
function notifyLocale() {
    snapshot = { locale, translator };
    for (const listener of listeners)
        listener();
}
export function setLocale(next) {
    locale = next;
    notifyLocale();
}
/** Native clients install a resource reader; web retains its bundled translations. */
export function configureLocaleTranslator(next, nextLocale) {
    translator = next;
    if (nextLocale !== undefined)
        locale = nextLocale;
    notifyLocale();
}
/** Interpolate after translation so user content and numbers never become translation keys. */
export function translateText(en, pt, values) {
    return translateWith(snapshot, en, pt, values);
}
function translateWith(current, en, pt, values) {
    const translated = current.translator?.(en, pt) ?? (current.locale.startsWith('pt') ? pt : en);
    if (values === undefined)
        return translated;
    return translated.replace(/%(\d+)\$s/g, (placeholder, position) => {
        const value = values[Number(position) - 1];
        return value === undefined ? placeholder : String(value);
    });
}
export function getLocale() {
    return locale;
}
export function subscribeLocale(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
export function useLocale() {
    const current = useSyncExternalStore(subscribeLocale, () => snapshot, () => snapshot);
    const text = useCallback((en, pt, values) => translateWith(current, en, pt, values), [current]);
    return { locale: current.locale, text };
}
/** How numbers are read and written in tables: `1.234,5` in Portuguese, `1,234.5` in English. */
export function numberFormatFor(current) {
    return new Intl.NumberFormat(current).format(1.1).includes(',')
        ? PORTUGUESE_NUMBERS
        : ENGLISH_NUMBERS;
}
const PORTUGUESE_NUMBERS = { decimal: ',' };
const ENGLISH_NUMBERS = { decimal: '.' };
//# sourceMappingURL=locale.js.map